import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { searchApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import { Avatar } from '@/components/ui/Avatar'
import { Dialog } from '@/components/ui/Dialog'
import { useDebounce } from '@/hooks/useDebounce'
import { useT } from '@/i18n'

import { useStartConversation } from '../hooks/useMessages'
import { cannotStartMessage } from '../reasons'

/** 新私信：输入用户名（带输入提示），选中即开始对话。 */
export function NewMessageDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const debounced = useDebounce(query.trim().replace(/^@/, ''), 200)
  const start = useStartConversation()

  const suggestions = useQuery({
    queryKey: keys.typeahead(`@${debounced}`),
    queryFn: () => searchApi.typeahead(`@${debounced}`),
    enabled: open && debounced.length > 0,
  })
  const users = suggestions.data?.users ?? []

  const pick = (username: string) => {
    start.mutate(username, {
      onSuccess: (conversation) => {
        onClose()
        navigate(`/messages/${conversation.id}`)
      },
    })
  }

  const error = start.error instanceof ApiError ? start.error : null

  return (
    <Dialog open={open} onClose={onClose} title={t('messages.newTitle')}>
      <div className="pb-4">
        <div className="px-4">
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              start.reset()
            }}
            placeholder={t('messages.searchPlaceholder')}
            aria-label={t('messages.searchPlaceholder')}
            autoFocus
            className="w-full rounded-md border border-line-strong bg-transparent px-3 py-2 text-[15px] outline-none focus:border-brand-ink"
          />
          {error && (
            <p role="alert" className="mt-2 text-sm text-danger">
              {cannotStartMessage(error, t)}
            </p>
          )}
        </div>
        <ul className="mt-2">
          {users.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                onClick={() => pick(user.username)}
                disabled={start.isPending}
                className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-surface-hover disabled:opacity-60"
              >
                <Avatar src={user.avatar_url} alt={user.display_name} size="sm" />
                <span className="min-w-0">
                  <span className="block truncate text-[15px] font-bold">{user.display_name}</span>
                  <span className="block truncate font-mono text-[14px] text-ink-muted">
                    @{user.username}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  )
}
